import ErrorComponent from "@/components/atoms/ErrorComponent";
import AreYouSurToContinueModal from "@/components/molecules/modals/AreYouSurToContinueModal";
import StoreSoftphoneLineModal from "@/components/molecules/modals/softphoneLine/StoreSoftphoneLineModal";
import AllSoftphoneLinesHeader from "@/components/molecules/table-headers/AllSoftphoneLinesHeader";
import Table from "@/components/molecules/table/Table";
import { isStrictAdmin } from "@/helpers/UserHelpers";
import { useDebouncedValue } from "@/hooks/useDebouncedValue";
import { useTableState } from "@/hooks/useTableState";
import SoftphoneLine from "@/interfaces/SoftphoneLine";
import { selectCurrentUser } from "@/services/slices/authSlice";
import {
  useDeleteSoftphoneLineMutation,
  useGetSoftphoneLinesQuery,
} from "@/services/softphoneLines";
import { faEdit, faTrash } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { FetchBaseQueryError } from "@reduxjs/toolkit/dist/query";
import { ColumnDef, TableState } from "@tanstack/react-table";
import { DateTime } from "luxon";
import { ElementRef, FC, Fragment, useMemo, useRef } from "react";
import { Button } from "react-bootstrap";
import { useSelector } from "react-redux";
import { ToastContentProps, toast } from "react-toastify";

const SoftphoneLinesView: FC = () => {
  const currentUser = useSelector(selectCurrentUser);
  const DeleteModalRef =
    useRef<ElementRef<typeof AreYouSurToContinueModal>>(null);
  const EditModalRef = useRef<ElementRef<typeof StoreSoftphoneLineModal>>(null);
  const [deleteSoftphoneLineAsync] = useDeleteSoftphoneLineMutation();

  const [state, setState] = useTableState<Partial<TableState>>(
    "softphoneLines",
    {
      pagination: { pageIndex: 0, pageSize: 15 },
      globalFilter: "",
    },
  );

  const debouncedQuery = useDebouncedValue(state.globalFilter, 500);

  const { data, isError } = useGetSoftphoneLinesQuery(
    {
      ...state.pagination,
      query: debouncedQuery,
      ...(state.sorting?.[0] && {
        sortBy: state.sorting[0].id,
        sortDirection: state.sorting[0].desc ? "desc" : "asc",
      }),
    },
    {
      skip: !isStrictAdmin(currentUser),
      pollingInterval: 5000,
      refetchOnFocus: true,
      refetchOnReconnect: true,
    },
  );

  const onDelete = async (id: string) => {
    toast.promise(deleteSoftphoneLineAsync(id).unwrap(), {
      pending: "Suppression de la ligne en cours...",
      success: {
        render() {
          DeleteModalRef.current?.showOrHiddenModal();
          return (
            <p className="mb-0 text-center">La ligne a bien été supprimée 🤩</p>
          );
        },
      },
      error: {
        render({ data }: ToastContentProps<FetchBaseQueryError>) {
          return (
            (data?.data as { message?: string })?.message ??
            "Une erreur est survenue"
          );
        },
      },
    });
  };

  const columns = useMemo<ColumnDef<SoftphoneLine>[]>(
    () => [
      {
        header: "Actions",
        enableSorting: false,
        footer: (props) => props.column.id,
        accessorFn: (line) => (
          <Fragment>
            <Button
              variant="info"
              size="sm"
              className="me-1"
              onClick={() => EditModalRef.current?.showOrHiddenModal(line)}
            >
              <FontAwesomeIcon icon={faEdit} />
            </Button>
            <Button
              variant="danger"
              size="sm"
              onClick={() => DeleteModalRef.current?.showOrHiddenModal(line.id)}
            >
              <FontAwesomeIcon icon={faTrash} />
            </Button>
          </Fragment>
        ),
        id: "actions",
        cell: (info) => info.getValue(),
      },
      {
        header: "Extension",
        footer: (props) => props.column.id,
        accessorFn: ({ kavkomExtension }) => kavkomExtension ?? "-",
        id: "kavkomExtension",
        cell: (info) => info.getValue(),
      },
      {
        header: "Libellé",
        footer: (props) => props.column.id,
        accessorFn: ({ label }) => label ?? "-",
        id: "label",
        cell: (info) => info.getValue(),
      },
      {
        header: "Assignée à",
        footer: (props) => props.column.id,
        accessorFn: ({ users, user }) => {
          const accounts = users ?? (user ? [user] : []);
          return accounts.length
            ? accounts
                .map(
                  (account) =>
                    `${account.fullname} — ${account.organisation?.name ?? "Sans organisme"} (${account.email})`,
                )
                .join(" ; ")
            : "Non-assignée";
        },
        id: "user",
        cell: (info) => info.getValue(),
      },
      {
        header: "Statut",
        footer: (props) => props.column.id,
        accessorFn: ({ status }) =>
          status === "active" ? "Active" : "Inactive",
        id: "status",
        cell: (info) => info.getValue(),
      },
      {
        header: "En appel",
        footer: (props) => props.column.id,
        accessorFn: ({ openCallLogs }) => openCallLogs.length > 0,
        id: "openCallLogs",
        cell: (info) =>
          isError
            ? "Statut indisponible"
            : info.getValue()
              ? "🟢 En appel"
              : "Disponible",
      },
      {
        header: "Créé le",
        footer: (props) => props.column.id,
        accessorFn: (row) =>
          DateTime.fromISO(row.createdAt).toFormat("dd-MM-yyyy"),
        id: "createdAt",
        cell: (info) => info.getValue(),
      },
    ],
    [isError],
  );

  if (!isStrictAdmin(currentUser)) {
    return (
      <ErrorComponent error={{ status: 403, data: {} }} absolute returnButton />
    );
  }

  return (
    <div className="formalys-page formalys-list-page">
      <StoreSoftphoneLineModal ref={EditModalRef} />
      <AreYouSurToContinueModal
        ref={DeleteModalRef}
        onContinue={onDelete}
        title="Souhaitez-vous vraiment supprimer la ligne softphone qui a pour id : {{id}} ?"
      />
      <Table
        resource="softphone-lines"
        HeaderComponent={AllSoftphoneLinesHeader}
        columns={columns}
        state={state}
        tableName="softphoneLines"
        onStateChange={setState}
        data={data?.data}
        meta={data?.meta}
      />
    </div>
  );
};

export default SoftphoneLinesView;
